import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
    LayoutDashboard, Receipt, RefreshCw, Target, ArrowLeftRight,
    CalendarDays, BarChart3, Settings as SettingsIcon, LogOut, Plus,
    Search, Bell, Wallet, Sun, Moon, Monitor, Crown, Gauge, CandlestickChart, Menu,
    Home, User, SlidersHorizontal, Users, Palette, Lock, Sparkles, Shield, LifeBuoy, Calculator, BookOpen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { useTheme } from "@/contexts/ThemeContext";
import { hasProAccess } from "@/lib/plans";
import QuickAddDialog from "@/components/QuickAddDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

// `color` gives each section its own icon color (sidebar, mobile hamburger sheet, bottom nav).
// Exported so the mobile "Home" menu page (see pages/MobileMenu.jsx) can reuse the same list.
// `pro: true` marks pages gated to the Pro/Family plans (see lib/plans.js) — SidebarNav
// shows a small lock badge on these for Free-plan users instead of hiding them outright,
// so the feature stays discoverable and links straight to an upsell (see UpsellCard).
export const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", color: "#4F46E5" },
    { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses", color: "#EF4444" },
    { to: "/subscriptions", label: "Subscriptions", icon: RefreshCw, testid: "nav-subscriptions", color: "#8B5CF6" },
    { to: "/budgets", label: "Budgets", icon: Gauge, testid: "nav-budgets", color: "#F59E0B" },
    { to: "/tools", label: "Tools", icon: Calculator, testid: "nav-tools", color: "#0D9488" },
    { to: "/investments", label: "Investments", icon: CandlestickChart, testid: "nav-investments", color: "#10B981", pro: true },
    { to: "/funds", label: "Sinking Funds", icon: Target, testid: "nav-funds", color: "#EC4899", pro: true },
    { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "nav-transactions", color: "#0EA5E9" },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar", color: "#14B8A6" },
    { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics", color: "#F97316", pro: true },
];
const SETTINGS_COLOR = "#64748B";
const CONTACT_COLOR = "#0891B2";
const HELP_COLOR = "#6366F1";

// Explicit (not sliced) so adding new nav items above doesn't silently bump
// something off the mobile bottom nav's fixed 5 slots. Tools (free for all plans)
// takes the 5th slot instead of Sinking Funds (Pro-gated) — Sinking Funds stays
// reachable from the sidebar/hamburger menu, just not pinned to the bottom bar.
const MOBILE_NAV_PATHS = ["/dashboard", "/expenses", "/subscriptions", "/budgets", "/tools"];
const MOBILE_NAV = MOBILE_NAV_PATHS.map(p => NAV.find(n => n.to === p)).filter(Boolean);

const ADMIN_COLOR = "#E11D48";

// Shared nav list (used by the desktop sidebar and the mobile hamburger sheet).
function SidebarNav({ hasPro, isAdmin, onNavigate }) {
    return (
        <>
            <nav className="flex-1 px-3 space-y-1">
                {NAV.map(({ to, label, icon: Icon, testid, color, pro }) => (
                    <NavLink
                        key={to} to={to} end={to === "/"} data-testid={testid} onClick={onNavigate}
                        style={({ isActive }) => (isActive ? { backgroundColor: `${color}16` } : undefined)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                             ${isActive
                                ? "text-slate-900"
                                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
                        }
                    >
                        <Icon className="w-[18px] h-[18px] flex-shrink-0" style={{ color }} />
                        <span className="flex-1">{label}</span>
                        {pro && !hasPro && <Lock className="w-3 h-3 flex-shrink-0 text-slate-400" data-testid={`${testid}-locked`} />}
                    </NavLink>
                ))}
            </nav>
            <div className="p-3 border-t border-slate-200/70 space-y-1">
                {!hasPro && (
                    <Link
                        to="/pricing" onClick={onNavigate} data-testid="upgrade-banner"
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white transition-all shadow-sm shadow-amber-500/20 group"
                    >
                        <Crown className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <div className="min-w-0">
                            <div className="text-sm font-semibold leading-tight">Upgrade plan</div>
                            <div className="text-[10px] opacity-80 leading-tight">Unlock premium features</div>
                        </div>
                    </Link>
                )}
                {isAdmin && (
                    <NavLink
                        to="/admin" data-testid="nav-admin" onClick={onNavigate}
                        style={({ isActive }) => (isActive ? { backgroundColor: `${ADMIN_COLOR}16` } : undefined)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                             ${isActive ? "text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
                        }
                    >
                        <Shield className="w-[18px] h-[18px] flex-shrink-0" style={{ color: ADMIN_COLOR }} />
                        <span>Admin</span>
                    </NavLink>
                )}
                <NavLink
                    to="/help" data-testid="nav-help" onClick={onNavigate}
                    style={({ isActive }) => (isActive ? { backgroundColor: `${HELP_COLOR}16` } : undefined)}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                         ${isActive ? "text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
                    }
                >
                    <BookOpen className="w-[18px] h-[18px] flex-shrink-0" style={{ color: HELP_COLOR }} />
                    <span>Help &amp; tips</span>
                </NavLink>
                <NavLink
                    to="/contact" data-testid="nav-contact" onClick={onNavigate}
                    style={({ isActive }) => (isActive ? { backgroundColor: `${CONTACT_COLOR}16` } : undefined)}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                         ${isActive ? "text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
                    }
                >
                    <LifeBuoy className="w-[18px] h-[18px] flex-shrink-0" style={{ color: CONTACT_COLOR }} />
                    <span>Contact Us</span>
                </NavLink>
                <NavLink
                    to="/settings" data-testid="nav-settings" onClick={onNavigate}
                    style={({ isActive }) => (isActive ? { backgroundColor: `${SETTINGS_COLOR}16` } : undefined)}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
                         ${isActive ? "text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
                    }
                >
                    <SettingsIcon className="w-[18px] h-[18px] flex-shrink-0" style={{ color: SETTINGS_COLOR }} />
                    <span>Settings</span>
                </NavLink>
            </div>
        </>
    );
}

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const { isMember } = useHousehold();
    // Household members get full Pro access under the owner's plan without their own
    // paid plan — see lib/plans.js's hasProAccess() and CLAUDE.md's "Plans" section.
    const hasPro = hasProAccess(user, isMember);
    const isAdmin = user?.role === "admin";
    const { mode, setMode, effectiveMode } = useTheme();
    const [quickOpen, setQuickOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const cycleTheme = () => {
        const next = { light: "dark", dark: "system", system: "light" };
        setMode(next[mode]);
    };
    const ThemeIcon = mode === "dark" ? Moon : mode === "system" ? Monitor : Sun;

    const cycleMode = () => {
        const next = mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
        setMode(next);
    };
    const ModeIcon = mode === "system" ? Monitor : effectiveMode === "dark" ? Moon : Sun;

    return (
        <div className="min-h-screen bg-background">
            {/* Sidebar (desktop) */}
            <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-slate-200/70 bg-white flex-col z-30" data-testid="sidebar">
                <div className="px-6 pt-7 pb-6">
                    <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="brand-link">
                        <span className="w-9 h-9 rounded-xl gradient-indigo grid place-items-center text-white shadow-sm group-hover:scale-105 transition-transform">
                            <Wallet className="w-5 h-5" />
                        </span>
                        <span className="font-display font-extrabold text-xl tracking-tight text-slate-900">
                            BudgetWise
                        </span>
                    </Link>
                </div>
                <SidebarNav hasPro={hasPro} isAdmin={isAdmin} />
            </aside>

            {/* Mobile hamburger nav sheet */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetContent side="left" className="p-0 w-72 flex flex-col lg:hidden" data-testid="mobile-sidebar">
                    <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                    <div className="px-6 pt-7 pb-6">
                        <Link to="/dashboard" className="flex items-center gap-2.5 group" onClick={() => setMobileNavOpen(false)}>
                            <span className="w-9 h-9 rounded-xl gradient-indigo grid place-items-center text-white shadow-sm">
                                <Wallet className="w-5 h-5" />
                            </span>
                            <span className="font-display font-extrabold text-xl tracking-tight text-slate-900">
                                BudgetWise
                            </span>
                        </Link>
                    </div>
                    <SidebarNav hasPro={hasPro} isAdmin={isAdmin} onNavigate={() => setMobileNavOpen(false)} />
                </SheetContent>
            </Sheet>

            {/* Top bar */}
            <header className="lg:pl-64 sticky top-0 z-20 glass" data-testid="topbar">
                <div className="flex items-center justify-between px-4 lg:px-8 h-16">
                    <div className="flex items-center gap-3 lg:hidden">
                        <button
                            onClick={() => setMobileNavOpen(true)}
                            data-testid="mobile-nav-trigger"
                            aria-label="Open navigation menu"
                            className="w-9 h-9 -ml-1 rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900 grid place-items-center transition-all"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                        <Link to="/dashboard" className="flex items-center gap-3" data-testid="mobile-brand-link" aria-label="Go to Dashboard">
                            <span className="w-9 h-9 rounded-xl gradient-indigo grid place-items-center text-white">
                                <Wallet className="w-5 h-5" />
                            </span>
                            <span className="font-display font-extrabold text-lg tracking-tight text-slate-900">BudgetWise</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-2 lg:ml-auto">
                        <button
                            onClick={cycleMode}
                            data-testid="theme-toggle"
                            title={`Theme: ${mode}`}
                            className="hidden sm:inline-flex w-10 h-10 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-900 items-center justify-center transition-all"
                        >
                            <ModeIcon className="w-[18px] h-[18px]" />
                        </button>
                        <Button
                            data-testid="quick-add-fab"
                            onClick={() => setQuickOpen(true)}
                            className="hidden sm:inline-flex bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm gap-1.5 h-10 px-4 active:scale-95 transition-all"
                        >
                            <Plus className="w-4 h-4" /> Quick add
                        </Button>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="rounded-full ring-1 ring-slate-200 hover:ring-slate-300 transition" data-testid="user-menu">
                                    <Avatar className="w-9 h-9">
                                        <AvatarImage src={user?.avatar_url} alt={user?.name} />
                                        <AvatarFallback className="bg-indigo-100 text-indigo-700 font-semibold">
                                            {user?.name?.[0]?.toUpperCase() || "U"}
                                        </AvatarFallback>
                                    </Avatar>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="font-medium text-slate-900">{user?.name}</div>
                                    <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {/* Mobile-only — desktop already has the full sidebar, so this adds nothing there. */}
                                <DropdownMenuItem asChild data-testid="menu-home" className="lg:hidden">
                                    <Link to="/menu"><Home className="w-4 h-4 mr-2" />Home</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild data-testid="menu-profile">
                                    <Link to="/settings?tab=profile"><User className="w-4 h-4 mr-2" />Profile</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild data-testid="menu-preferences">
                                    <Link to="/settings?tab=preferences"><SlidersHorizontal className="w-4 h-4 mr-2" />Preferences</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild data-testid="menu-family">
                                    <Link to="/settings?tab=household"><Users className="w-4 h-4 mr-2" />Family</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild data-testid="menu-appearance">
                                    <Link to="/settings?tab=appearance"><Palette className="w-4 h-4 mr-2" />Appearance</Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild data-testid="menu-pricing">
                                    <Link to="/pricing"><Sparkles className="w-4 h-4 mr-2" />Plans & billing</Link>
                                </DropdownMenuItem>
                                {isAdmin && (
                                    <DropdownMenuItem asChild data-testid="menu-admin">
                                        <Link to="/admin"><Shield className="w-4 h-4 mr-2" />Admin</Link>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-rose-600 focus:text-rose-700">
                                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            <main className="lg:pl-64 pb-24 lg:pb-12" data-testid="main-content">
                <div className="px-4 lg:px-8 pt-1.5 pb-3 lg:pt-3 lg:pb-5 max-w-[1400px] mx-auto">
                    {children}
                </div>
            </main>

            {/* Bottom nav (mobile) */}
            <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-slate-200/70" data-testid="bottom-nav">
                <div className="grid grid-cols-5">
                    {MOBILE_NAV.map(({ to, label, icon: Icon, testid, color }) => (
                        <NavLink
                            key={to} to={to} end={to === "/"} data-testid={`m-${testid}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center py-2.5 gap-0.5 text-[10px] font-medium transition-opacity ${
                                    isActive ? "text-slate-900" : "text-slate-500 opacity-60"
                                }`
                            }
                        >
                            <Icon className="w-5 h-5" style={{ color }} />
                            <span>{label.split(" ")[0]}</span>
                        </NavLink>
                    ))}
                </div>
            </nav>

            {/* Mobile FAB */}
            <button
                data-testid="mobile-quick-add-fab"
                onClick={() => setQuickOpen(true)}
                className="lg:hidden fixed bottom-20 right-5 z-40 w-14 h-14 rounded-full gradient-indigo text-white shadow-lg shadow-indigo-500/30 grid place-items-center active:scale-95 transition-all"
            >
                <Plus className="w-6 h-6" />
            </button>

            <QuickAddDialog open={quickOpen} onOpenChange={setQuickOpen} />
        </div>
    );
}
